/**
 * Users API Service
 * Handles all user-related API calls
 */

import axiosInstance from '@/lib/axios'
import API_ROUTES from '@/lib/api-routes'
import type {
    UserListResponse,
    UserFilters,
    UserPersonalDetailResponse,
    UserTransactionHistoryResponse,
    UserTransactionFilters,
    FlagUserRequest,
    FlagUserResponse,
    UnflagUserResponse,
} from '@/types/user'

/**
 * Get Users List
 * GET /api/v1/admin/users
 * @param filters - Optional query parameters for filtering users
 */
export const getUsers = async (
    filters?: UserFilters
): Promise<UserListResponse> => {
    const response = await axiosInstance.get<UserListResponse>(
        API_ROUTES.USERS.LIST,
        {
            params: filters,
        }
    )
    return response.data
}

/**
 * Get User Personal Details
 * GET /api/v1/admin/users/{id}/personal-detail
 * Response envelope: { status, data: { success, data: UserPersonalDetailResponse } }
 * @param id - User ID
 */
export const getUserPersonalDetail = async (
    id: string
): Promise<UserPersonalDetailResponse> => {
    const response = await axiosInstance.get<{
        status: string
        data: { success: boolean; data: UserPersonalDetailResponse }
    }>(API_ROUTES.USERS.PERSONAL_DETAIL(id))
    return response.data.data.data
}

/**
 * Get User Transaction History
 * GET /api/v1/admin/users/{id}/user-transactions-history
 * Response envelope: { status, data: { success, data: [], pagination } }
 * @param id - User ID
 * @param filters - Optional query parameters for filtering transactions
 */
export const getUserTransactionHistory = async (
    id: string,
    filters?: UserTransactionFilters
): Promise<UserTransactionHistoryResponse> => {
    const response = await axiosInstance.get<{
        status: string
        data: UserTransactionHistoryResponse
    }>(API_ROUTES.USERS.TRANSACTION_HISTORY(id), { params: filters })
    return response.data.data
}

/**
 * Flag a User
 * PATCH /api/v1/admin/users/{id}/flag
 * @param id - User ID
 * @param data - Flag request with reason
 */
export const flagUser = async (
    id: string,
    data: FlagUserRequest
): Promise<FlagUserResponse> => {
    const response = await axiosInstance.patch<FlagUserResponse>(
        API_ROUTES.USERS.FLAG(id),
        data
    )
    return response.data
}

/**
 * Unflag a User
 * PATCH /api/v1/admin/users/{id}/unflag
 * @param id - User ID
 */
export const unflagUser = async (id: string): Promise<UnflagUserResponse> => {
    const response = await axiosInstance.patch<UnflagUserResponse>(
        API_ROUTES.USERS.UNFLAG(id)
    )
    return response.data
}
